/**
 * The layers of a tile's column, for its wall: the kind of each band below its top, one height
 * level each, packed for the surface shader four kinds to a float. The first band is the tile's own
 * block, the one its top shows (under grass, a tree or water: the soil they grow in); then the
 * column below it, level by level: blocks laid on it (rules 27, above the surface too), the
 * planet's layers (its surface layer as the soil grass grows in), down to the core, and past the
 * core the core goes on. So the side of a tile beside a pit shows what that tile is made of, and
 * then what lies under it. Without layers (the welcome planet) the column says so and the wall
 * keeps the tile's own colour. Pure, so it is tested without a renderer.
 */
import { digsOf, type Kind } from '@bramvasterd/terrella-rules';

import type { PlanetView } from './view';

/** Bands the shader paints below a tile's top; deeper than that, the last one goes on. */
export const BANDS = 16;
/**
 * Four kinds to a float, the first band in the lowest digit: a + b * KIND_BASE + c * KIND_BASE²
 * + d * KIND_BASE³, 20 bits, exact in a float32 (the shader reads it unblended, `flat`). Kinds
 * from KIND_BASE up are drawn as the last one, grey like any kind past the palette.
 */
export const KIND_BASE = 32;
const PER_FLOAT = 4;

export type Column = [number, number, number, number];

/** The kind of band `band` (0 right under the top: the tile's own block) of tile `i`. */
export function bandKind(view: PlanetView, i: number, band: number): number {
  const layers = view.layers;
  const own = view.kind[i] ?? 0;
  if (!layers) return own;
  // the block its top shows is the first band; surface life (grass, a tree) and water on soil
  if (band === 0) return digsOf(own as Kind);
  // the depth of the slab this band is: the tile's floor depth, and one more a band
  const depth = Math.min(-(view.elev[i] ?? 0) + band, layers.coreDepth);
  return digsOf(layers.at(i, depth) as Kind);
}

/** Tile `i`'s column for the shader; x below 0 when no layers are known. */
export function packColumn(view: PlanetView, i: number, out: Column): Column {
  if (!view.layers) {
    out[0] = -1;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    return out;
  }
  for (let k = 0; k < 4; k++) {
    let packed = 0;
    for (let j = PER_FLOAT - 1; j >= 0; j--)
      packed = packed * KIND_BASE + Math.min(bandKind(view, i, PER_FLOAT * k + j), KIND_BASE - 1);
    out[k] = packed;
  }
  return out;
}
